import { pinyin } from 'pinyin-pro';

/**
 * 将汉字转换为拼音
 * @param {string} text - 输入文本
 * @param {object} options - 配置选项
 * @returns {array} 转换结果数组
 */
export function convertToPinyin(text, options = {}) {
  const {
    toneType = 'symbol', // 'symbol' | 'num' | 'none'
    type = 'array',
    multiple = true,
    mode = 'normal' // 'normal' | 'surname'
  } = options;

  if (!text || typeof text !== 'string') {
    return [];
  }

  // 分割文本为单个字符（保留换行符）
  const characters = text.split(/(?=[\s\S])/u);

  const results = characters.map((char, index) => {
    // 检查是否为换行符
    if (char === '\n' || char === '\r\n') {
      return {
        char: '\n',
        pinyin: '',
        allPinyins: [],
        selectedPinyinIndex: 0,
        isHanzi: false,
        isLineBreak: true,
        isSpace: false,
        tone: 0,
        index
      };
    }

    // 检查是否为空格
    if (char === ' ' || char === '　') {
      return {
        char: ' ',
        pinyin: '',
        allPinyins: [],
        selectedPinyinIndex: 0,
        isHanzi: false,
        isLineBreak: false,
        isSpace: true,
        isPunctuation: false,
        tone: 0,
        index
      };
    }

    // 检查是否为汉字（Unicode范围：4E00-9FFF）
    const isHanzi = /[一-鿿]/.test(char);

    // 非汉字（包括标点符号）不显示格子
    if (!isHanzi) {
      return {
        char,
        pinyin: '',
        allPinyins: [],
        selectedPinyinIndex: 0,
        isHanzi: false,
        isLineBreak: false,
        isSpace: false,
        isPunctuation: true,
        tone: 0,
        index
      };
    }

    // 获取拼音
    const pinyinResult = pinyin(char, {
      toneType,
      type: multiple ? 'array' : 'string',
      multiple,
      mode
    });

    // 解析拼音，保留多音字候选
    const allPinyins = Array.isArray(pinyinResult)
      ? [...new Set(pinyinResult.filter(Boolean))]
      : String(pinyinResult || '').split(/\s+/).filter(Boolean);
    const pinyinStr = allPinyins[0] || char;
    const tone = toneType === 'symbol' ? 0 : extractTone(pinyinStr);

    return {
      char,
      pinyin: pinyinStr || char,
      allPinyins: allPinyins.length > 0 ? allPinyins : [pinyinStr || char],
      selectedPinyinIndex: 0,
      isHanzi: true,
      tone,
      index
    };
  });

  return results;
}

/**
 * 提取声调数字
 * @param {string} pinyinStr - 拼音字符串
 * @returns {number} 声调数字 (0-4)
 */
function extractTone(pinyinStr) {
  if (!pinyinStr) return 0;

  // 带声调符号的拼音
  const toneMap = {
    'ā': 1, 'á': 2, 'ǎ': 3, 'à': 4,
    'ē': 1, 'é': 2, 'ě': 3, 'è': 4,
    'ī': 1, 'í': 2, 'ǐ': 3, 'ì': 4,
    'ō': 1, 'ó': 2, 'ǒ': 3, 'ò': 4,
    'ū': 1, 'ú': 2, 'ǔ': 3, 'ù': 4,
    'ǖ': 1, 'ǘ': 2, 'ǚ': 3, 'ǜ': 4
  };

  for (const char of pinyinStr) {
    if (toneMap[char]) {
      return toneMap[char];
    }
  }

  // 检查数字声调（如 "ma1"）
  const lastChar = pinyinStr.slice(-1);
  if (/[1-4]/.test(lastChar)) {
    return parseInt(lastChar);
  }

  return 0; // 轻声
}

/**
 * 将拼音转换为带声调符号的格式
 * @param {string} pinyin - 拼音字符串
 * @param {number} tone - 声调数字
 * @returns {string} 带声调的拼音
 */
export function formatPinyinWithTone(pinyin, tone) {
  if (!pinyin || tone === 0) return pinyin;

  // 元音映射表
  const vowelTones = {
    'a': ['ā', 'á', 'ǎ', 'à'],
    'e': ['ē', 'é', 'ě', 'è'],
    'i': ['ī', 'í', 'ǐ', 'ì'],
    'o': ['ō', 'ó', 'ǒ', 'ò'],
    'u': ['ū', 'ú', 'ǔ', 'ù'],
    'ü': ['ǖ', 'ǘ', 'ǚ', 'ǜ']
  };

  // 找到主要元音
  const vowels = ['a', 'o', 'e', 'i', 'u', 'ü'];
  let targetVowel = 'a'; // 默认

  for (const vowel of vowels) {
    if (pinyin.includes(vowel)) {
      targetVowel = vowel;
      if (vowel === 'a' || vowel === 'o' || vowel === 'e') {
        break; // a, o, e 优先级最高
      }
    }
  }

  // 替换元音
  const tonedVowel = vowelTones[targetVowel]?.[tone - 1];
  if (tonedVowel) {
    return pinyin.replace(targetVowel, tonedVowel);
  }

  return pinyin;
}

/**
 * 验证输入是否包含汉字
 * @param {string} text - 输入文本
 * @returns {boolean} 是否包含汉字
 */
export function containsHanzi(text) {
  return /[一-鿿]/.test(text);
}

/**
 * 统计汉字数量
 * @param {string} text - 输入文本
 * @returns {number} 汉字数量
 */
export function countHanzi(text) {
  const matches = text.match(/[一-鿿]/g);
  return matches ? matches.length : 0;
}

/**
 * 清理非汉字字符（可选）
 * @param {string} text - 输入文本
 * @param {boolean} keepPunctuation - 是否保留标点
 * @returns {string} 清理后的文本
 */
export function cleanText(text, keepPunctuation = true) {
  if (keepPunctuation) {
    // 保留汉字和中文标点
    return text.replace(/[^一-鿿　-〿＀-￯\s]/g, '');
  }
  // 只保留汉字
  return text.replace(/[^一-鿿]/g, '');
}

export default {
  convertToPinyin,
  formatPinyinWithTone,
  containsHanzi,
  countHanzi,
  cleanText
};
